// «Ayer en 10 segundos» — stories a pantalla completa con auto-avance,
// tap izquierda/derecha para navegar, MANTENER PULSADO para pausar (se reanuda
// al soltar, como las stories de toda la vida) y barras de progreso.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Easing, Modal, Pressable, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SLIDES as SLIDES_DEMO, type Slide } from '@/constants/data';
import { C, F } from '@/constants/theme';
import Tortugona from './Tortugona';
import { WavesMark } from './Waves';

const DUR = 3400;

export default function StoriesModal({ visible, onClose, slides }: {
  visible: boolean; onClose: () => void; slides?: Slide[];
}) {
  const insets = useSafeAreaInsets();
  const SLIDES = slides && slides.length ? slides : SLIDES_DEMO;
  const [idx, setIdx] = useState(0);
  const prog = useRef(new Animated.Value(0)).current;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idxRef = useRef(0);          // índice vivo para el timeout (evita cierres viejos)
  const restante = useRef(DUR);      // ms que le quedan al slide actual
  const inicioTramo = useRef(0);     // reloj al (re)arrancar el tramo
  const enPausa = useRef(false);     // true mientras el dedo mantiene pulsado

  const goRef = useRef<(i: number) => void>(() => {});

  const arranca = useCallback((ms: number) => {
    inicioTramo.current = Date.now();
    restante.current = ms;
    Animated.timing(prog, { toValue: 1, duration: ms, easing: Easing.linear, useNativeDriver: false }).start();
    timer.current = setTimeout(() => goRef.current(idxRef.current + 1), ms);
  }, [prog]);

  const go = useCallback((i: number) => {
    if (timer.current) clearTimeout(timer.current);
    if (i >= SLIDES.length) { onClose(); return; }
    idxRef.current = i;
    setIdx(i);
    prog.setValue(0);
    arranca(DUR);
  }, [onClose, prog, SLIDES.length, arranca]);
  goRef.current = go;

  /* Mantener pulsado = congelar barra y auto-avance; soltar = seguir donde iba. */
  const pausa = useCallback(() => {
    if (enPausa.current) return;
    enPausa.current = true;
    if (timer.current) clearTimeout(timer.current);
    prog.stopAnimation();
    restante.current = Math.max(300, restante.current - (Date.now() - inicioTramo.current));
  }, [prog]);
  const reanuda = useCallback(() => {
    if (!enPausa.current) return;
    enPausa.current = false;
    arranca(restante.current);
  }, [arranca]);

  useEffect(() => {
    if (visible) { enPausa.current = false; go(0); }
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [visible, go]);

  const sl = SLIDES[Math.min(idx, SLIDES.length - 1)];

  return (
    <Modal visible={visible} animationType="fade" onRequestClose={onClose}>
      <View style={[s.root, { backgroundColor: sl.colors[1] }]}>
        {/* degradado simple: capa superior */}
        <View style={[StyleSheet.absoluteFill, { backgroundColor: sl.colors[0], opacity: 0.55 }]} />

        <View style={[s.progRow, { marginTop: insets.top + 10 }]}>
          {SLIDES.map((_, i) => (
            <View key={i} style={s.progBg}>
              {i < idx ? <View style={[s.progFill, { width: '100%' }]} /> : null}
              {i === idx ? (
                <Animated.View style={[s.progFill, { width: prog.interpolate({ inputRange: [0, 1], outputRange: ['0%', '100%'] }) }]} />
              ) : null}
            </View>
          ))}
        </View>

        <Pressable style={[s.close, { top: insets.top + 26 }]} onPress={onClose} hitSlop={12}>
          <Text style={{ color: '#fff', fontSize: 15 }}>✕</Text>
        </Pressable>

        <View style={s.body}>
          {sl.tortugona ? <Tortugona metal="oro" size={52} /> : null}
          <Text style={s.kick}>{sl.kick.toUpperCase()}</Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Text style={s.h}>{sl.h}</Text>
            {sl.waves ? <WavesMark width={42} /> : null}
          </View>
          <Text style={s.sub}>{sl.sub}</Text>
          <Text style={s.foot}>{sl.foot}</Text>
        </View>

        {/* tap corto = navegar · mantener pulsado (≥180 ms) = pausa, soltar = sigue */}
        <TapZona style={s.tapL} onTap={() => go(Math.max(0, idx - 1))} onHold={pausa} onRelease={reanuda} />
        <TapZona style={s.tapR} onTap={() => go(idx + 1)} onHold={pausa} onRelease={reanuda} />

        <View style={{ position: 'absolute', bottom: 24 + insets.bottom, left: 34, opacity: 0.5 }}>
          <WavesMark width={64} />
        </View>
      </View>
    </Modal>
  );
}

/* Zona táctil con la semántica de stories: distingue tap corto (navega) de
   mantener pulsado (pausa). onPress se ignora si hubo hold, para que soltar
   tras leer NO salte de slide. */
function TapZona({ style, onTap, onHold, onRelease }: {
  style: object; onTap: () => void; onHold: () => void; onRelease: () => void;
}) {
  const held = useRef(false);
  return (
    <Pressable
      style={style}
      delayLongPress={180}
      onPressIn={() => { held.current = false; }}
      onLongPress={() => { held.current = true; onHold(); }}
      onPressOut={() => { if (held.current) onRelease(); }}
      onPress={() => { if (!held.current) onTap(); }}
    />
  );
}

const s = StyleSheet.create({
  root: { flex: 1 },
  progRow: { flexDirection: 'row', gap: 5, paddingHorizontal: 16 },
  progBg: { flex: 1, height: 3, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.22)', overflow: 'hidden' },
  progFill: { height: '100%', backgroundColor: '#fff' },
  close: { position: 'absolute', right: 14, zIndex: 5, width: 34, height: 34, borderRadius: 17, backgroundColor: 'rgba(255,255,255,0.14)', alignItems: 'center', justifyContent: 'center' },
  body: { flex: 1, justifyContent: 'center', paddingHorizontal: 34, paddingBottom: 90, gap: 14 },
  kick: { fontFamily: F.uiSemi, fontSize: 12, letterSpacing: 2.5, color: 'rgba(255,255,255,0.65)' },
  h: { fontFamily: F.display, fontSize: 42, lineHeight: 48, color: '#fff' },
  sub: { fontFamily: F.ui, fontSize: 20, color: 'rgba(255,255,255,0.78)', lineHeight: 27 },
  foot: { fontFamily: F.ui, fontSize: 13.5, color: 'rgba(255,255,255,0.75)', lineHeight: 21 },
  tapL: { position: 'absolute', left: 0, top: 90, bottom: 0, width: '35%' },
  tapR: { position: 'absolute', right: 0, top: 90, bottom: 0, width: '65%' },
});
